/**
 * Where the URLs listing was left, so a URL page can send the operator back to
 * it rather than to an unfiltered list.
 *
 * Kept in `sessionStorage` rather than in router state on purpose. Router
 * state is lost the moment the operator reloads the URL page, opens it in a
 * new tab from a row, or arrives from the ⌘K palette — and those are exactly
 * the moments a filtered list of eight hundred URLs is worth remembering. A
 * session-scoped key survives all three and dies with the tab, which is the
 * right lifetime for "the filter you had selected previously".
 *
 * Every read is defensive: `sessionStorage` throws in a private window with
 * site data blocked, and a wrong "back" link must never be the reason a page
 * fails to render.
 */

const KEY = 'redirector:urls:list';

/** The listing with no filter — what "back" means when nothing is remembered. */
export const URLS_LIST_PATH = '/urls';

/**
 * Records the listing's current query string.
 *
 * Takes the search string (`?group=3&q=promo`, or empty) rather than a whole
 * path, so it cannot be pointed at some other screen.
 */
export function rememberUrlsList(search: string): void {
  try {
    sessionStorage.setItem(KEY, search);
  } catch {
    // Storage denied. Back then falls back to the unfiltered listing.
  }
}

/** The remembered listing path, or the unfiltered listing. */
export function urlsListPath(): string {
  let search = '';
  try {
    search = sessionStorage.getItem(KEY) ?? '';
  } catch {
    return URLS_LIST_PATH;
  }

  // A stored value that is not a query string is ignored rather than trusted:
  // it can only have come from a different version of this key.
  if (search === '' || !search.startsWith('?')) return URLS_LIST_PATH;
  return `${URLS_LIST_PATH}${search}`;
}
